import {
  Activity,
  AlertTriangle,
  BarChart2,
  Bell,
  BellDot,
  CheckSquare,
  ClipboardCheck,
  FolderOpen,
  Inbox,
  LayoutDashboard,
  LogOut,
  MessageSquarePlus,
  Moon,
  ScrollText,
  Search,
  Settings2,
  Sliders,
  Sun,
  Upload,
  Users,
  UsersRound,
  X,
  Zap,
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { Role } from '../../types/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  minRole?: Role;
}

const PERSONAL: NavItem[] = [
  { to: '/dashboard',    label: 'Dashboard',       icon: LayoutDashboard },
  { to: '/tasks',        label: 'Tasks',            icon: CheckSquare },
  { to: '/check-in',     label: 'Check In',         icon: ClipboardCheck },
  { to: '/pulse',        label: 'Pulse',            icon: Activity },
  { to: '/feedback/new', label: 'Submit Feedback',  icon: MessageSquarePlus },
];

const WORK: NavItem[] = [
  { to: '/projects', label: 'Projects', icon: FolderOpen },
  { to: '/sprints',  label: 'Sprints',  icon: Zap, minRole: 'team_lead' },
];

const COMMS: NavItem[] = [
  { to: '/notifications',             label: 'Notifications',         icon: Bell },
  { to: '/notifications/preferences', label: 'Notification Settings', icon: BellDot },
];

const MANAGEMENT: NavItem[] = [
  { to: '/engineers',         label: 'Engineers',      icon: Users,         minRole: 'project_manager' },
  { to: '/escalations',       label: 'Escalations',    icon: AlertTriangle, minRole: 'project_manager' },
  { to: '/import',            label: 'Import',         icon: Upload,        minRole: 'project_manager' },
  { to: '/reports/leadership',label: 'Report',         icon: BarChart2,     minRole: 'head_of_rd' },
  { to: '/feedback',          label: 'Feedback Inbox', icon: Inbox,         minRole: 'head_of_rd' },
];

const ADMIN: NavItem[] = [
  { to: '/admin/users',      label: 'Users',      icon: UsersRound, minRole: 'head_of_rd' },
  { to: '/admin/teams',      label: 'Teams',      icon: Settings2,  minRole: 'head_of_rd' },
  { to: '/admin/thresholds', label: 'Thresholds', icon: Sliders,    minRole: 'head_of_rd' },
  { to: '/admin/audit-log',  label: 'Audit Log',  icon: ScrollText, minRole: 'head_of_rd' },
];

interface SectionProps {
  label: string;
  items: NavItem[];
  canFn: (role: Role) => boolean;
  onNavClick: () => void;
}

function NavSection({ label, items, canFn, onNavClick }: SectionProps) {
  const visible = items.filter((i) => !i.minRole || canFn(i.minRole));
  if (visible.length === 0) return null;

  return (
    <div className="px-2">
      <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted-foreground/70">
        {label}
      </p>
      <ul role="list" className="space-y-0.5">
        {visible.map((item) => (
          <NavItemLink key={item.to} item={item} onClick={onNavClick} />
        ))}
      </ul>
    </div>
  );
}

function NavItemLink({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const Icon = item.icon;
  return (
    <li>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink
              to={item.to}
              onClick={onClick}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-sidebar-muted-foreground hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right" className="hidden">
            {item.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </li>
  );
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onOpenPalette: () => void;
}

export default function Sidebar({ open, onClose, onOpenPalette }: SidebarProps) {
  const { currentUser, can, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <aside
      className={cn(
        // Base: fixed drawer (mobile)
        'fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground',
        'transition-transform duration-200 ease-in-out',
        // Desktop: static, always visible, no animation needed
        'lg:static lg:z-auto lg:translate-x-0 lg:transition-none',
        // Mobile open/closed
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      {/* Logo */}
      <div className="flex h-14 items-center gap-2.5 border-b border-sidebar-border px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary shadow-md">
          <Zap className="h-4 w-4 text-white" />
        </div>
        <span className="flex-1 text-base font-bold tracking-tight">Cadence</span>
        {/* Close button — mobile only */}
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring lg:hidden"
          aria-label="Close navigation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-4 overflow-y-auto py-4" aria-label="Main navigation">
        <NavSection label="Personal"   items={PERSONAL}   canFn={can} onNavClick={onClose} />
        <NavSection label="Work"       items={WORK}       canFn={can} onNavClick={onClose} />
        <NavSection label="Comms"      items={COMMS}      canFn={can} onNavClick={onClose} />
        <NavSection label="Management" items={MANAGEMENT} canFn={can} onNavClick={onClose} />
        <NavSection label="Admin"      items={ADMIN}      canFn={can} onNavClick={onClose} />
      </nav>

      {/* User footer */}
      <div className="border-t border-sidebar-border p-3">
        {/* Command palette trigger */}
        <button
          type="button"
          onClick={onOpenPalette}
          className="mb-2 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-xs text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="rounded border border-sidebar-border bg-sidebar-muted px-1 py-0.5 text-[9px] leading-none">⌘K</kbd>
        </button>
        <Separator className="mb-3 bg-sidebar-border" />
        <div className="flex items-center gap-2.5 rounded-md px-1 py-1">
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground text-[10px]">
              {currentUser ? initials(currentUser.name) : '??'}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-sidebar-foreground">{currentUser?.name}</p>
            <p className="truncate text-[10px] text-sidebar-muted-foreground">{currentUser?.email}</p>
          </div>
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="shrink-0 rounded-md p-1.5 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={logout}
            title="Sign out"
            className="shrink-0 rounded-md p-1.5 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
